import { DateValue } from '@internationalized/date';
import { Color, Spacing } from '@persona-id/design-tokens';
import { AriaDateFieldProps, useDateField, useDateSegment } from '@react-aria/datepicker';
import { useLocale } from '@react-aria/i18n';
import { mergeRefs } from '@react-aria/utils';
import {
  DateFieldState,
  DateSegment as DateSegmentObj,
  SegmentType,
  useDateFieldState,
} from '@react-stately/datepicker';
import { forwardRef, useRef } from 'react';
import styled, { css } from 'styled-components';

import { createCalendar } from 'design-system/components/DatePicker/common';
import { isFocused } from 'design-system/lib/pseudoClass';
import inputMixin, { InputMixinProps } from 'design-system/mixins/input';
import PseudoClassProps from 'design-system/types/PseudoClassProps';

const DateSegmentContainer = styled.div<
  { $isComma?: boolean; $isTimezone?: boolean } & PseudoClassProps
>`
  font-variant-numeric: tabular-nums;
  box-sizing: content-box;
  text-align: end;

  ${isFocused} {
    outline: 0px solid transparent;
    background-color: ${Color.CorePurple200};
  }

  ${({ $isComma, $isTimezone }) =>
    ($isComma || $isTimezone) &&
    css`
      margin-left: 0.25ch;
      margin-right: 0.25ch;
    `}
`;

const DateSegmentPlaceholder = styled.span<{ isPlaceholder?: boolean; type: SegmentType }>`
  display: block;
  width: 100%;
  pointer-events: none;

  &:focus {
    outline: 0px solid transparent;
  }

  ${({ isPlaceholder }) =>
    isPlaceholder
      ? css`
          height: 100%;
          color: ${({ theme }) => theme.color.text.tertiary};
        `
      : css`
          visibility: hidden;
          height: 0;
        `}
  ${({ type }) =>
    type === 'literal' &&
    css`
      padding-left: ${Spacing[1]};
      padding-right: ${Spacing[1]};
    `}
  ${({ type, isPlaceholder }) =>
    type === 'month' &&
    isPlaceholder &&
    css`
      width: 3ch;
    `}
`;

interface DateSegmentProps {
  segment: DateSegmentObj;
  state: DateFieldState;
}

function DateSegment({ segment, state }: DateSegmentProps) {
  const ref = useRef();
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const { segmentProps } = useDateSegment(segment, state, ref);

  let minWidth: string;

  switch (segment.type) {
    case 'dayPeriod': {
      minWidth = '2.75ch';
      break;
    }
    default: {
      minWidth = `${segment.maxValue != null && String(segment.maxValue).length + 0.25}ch`;
      break;
    }
  }

  const isComma = segment.type === 'literal' && segment.text === ', ';

  return (
    <DateSegmentContainer
      {...segmentProps}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ref={ref}
      style={{
        ...segmentProps.style,
        minWidth,
      }}
      $isComma={isComma}
      $isTimezone={segment.type === 'timeZoneName'}
    >
      {/* Always reserve space for the placeholder, to prevent layout shift when editing. */}
      {segment.placeholder && (
        <DateSegmentPlaceholder
          aria-hidden='true'
          isPlaceholder={segment.isPlaceholder}
          type={segment.type}
        >
          {segment.placeholder}
        </DateSegmentPlaceholder>
      )}
      {segment.isPlaceholder ? '' : segment.text}
    </DateSegmentContainer>
  );
}

const InputBox = styled.div<InputMixinProps & { $inputPosition: 'start' | 'end' }>`
  ${inputMixin};
  display: flex;
  padding-left: ${Spacing[2]};
  padding-right: ${Spacing[2]};
  line-height: 1;

  ${({ $inputPosition }) => {
    switch ($inputPosition) {
      case 'start': {
        return css`
          border-top-right-radius: 0px;
          border-bottom-right-radius: 0px;
        `;
      }
      case 'end': {
        return css`
          border-left-color: transparent;
          border-top-left-radius: 0px;
          border-bottom-left-radius: 0px;
        `;
      }
    }
  }}
`;

interface DateFieldProps extends AriaDateFieldProps<DateValue> {
  inputPosition?: 'start' | 'end';
  focused?: boolean;
  readOnly?: boolean;
  value?: DateValue;
  disabled?: boolean;
}

export const DateField = forwardRef<HTMLDivElement, DateFieldProps>(function DateField(
  props: DateFieldProps,
  forwardedRef,
) {
  const { locale } = useLocale();
  const state = useDateFieldState({
    ...props,
    locale,
    createCalendar,
  });

  const ref = useRef<HTMLDivElement>();
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const { fieldProps } = useDateField(props, state, ref);

  return (
    <InputBox
      {...fieldProps}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ref={mergeRefs<HTMLDivElement>(ref, forwardedRef)}
      focused={props.focused}
      disabled={props.disabled}
      aria-label={props['aria-label']}
      $size='md'
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      $inputPosition={props.inputPosition}
    >
      {state.segments.map((segment, i) => (
        <DateSegment key={i} segment={segment} state={state} />
      ))}
    </InputBox>
  );
});

export default DateField;
